import { Analytics } from '@cobliteam/analytics';
import ChecklistDrawer from '@cobliteam/checklists/src/containers/ChecklistDrawer/ChecklistDrawer';
import { useUser } from '@cobliteam/hooks';
import { MATURITY_ITEMS, type MaturityAreaKey, type MaturityItemId } from '@cobliteam/hooks/src/useBasicValueMaturity/basicValueMaturityModel';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import {
  type BasicValueCriticality,
  type BasicValueStep,
  type BasicValueStepHelpers,
  DialogBasicValueOnboardingView,
} from '@cobliteam/ui-business/components/dialogs/DialogBasicValueOnboarding/DialogBasicValueOnboarding';
import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ITEM_ICON } from '../../pages/BasicValuePage/basicValueMaturityPresentation';
import { buildItemTourSteps } from '../../pages/BasicValuePage/basicValueTour';
import MaturityTrackCompact from '../../pages/BasicValuePage/components/MaturityTrackCompact';
import useBasicValueGuideStore from '../../store/useBasicValueGuideStore';
import useCursorFlowStore from '../../store/useCursorFlowStore';
import AlertStepAction from './steps/AlertStepAction';
import ChecklistStepAction from './steps/ChecklistStepAction';
import DeviceAssociationStepBody from './steps/DeviceAssociationStepBody';
import DriverAssociationStepBody from './steps/DriverAssociationStepBody';
import DriverStepBody from './steps/DriverStepBody';
import GroupStepBody from './steps/GroupStepBody';
import InstallationStepBody from './steps/InstallationStepBody';
import SpeedStepBody from './steps/SpeedStepBody';
import UsersStepBody from './steps/UsersStepBody';
import VehicleStepBody from './steps/VehicleStepBody';
import useBasicValueOnboardingSource, { type BasicValuePendingFeature } from './useBasicValueOnboardingSource';

const CRITICALITY_MAP: Record<string, BasicValueCriticality> = {
  CRITICAL: 'critical',
  URGENT: 'urgent',
  IMPORTANT: 'important',
  SUGGESTIVE: 'suggestive',
};

/** Setup stage (area) of each item, used to highlight the right node in the modal's track. */
const AREA_BY_ITEM_ID = MATURITY_ITEMS.reduce(
  (acc, item) => {
    acc[item.id] = item.area;
    return acc;
  },
  {} as Record<MaturityItemId, MaturityAreaKey>
);

/** Items resolved inline (no leaving the page); only geofences navigates (map drawing can't be a modal). */
const NAVIGATE_IDS = new Set<MaturityItemId>(['geofences']);

const BasicValueOnboardingConnected = () => {
  const history = useHistory();
  const { t } = useTranslation('basic-value-onboarding');

  const openItemId = useBasicValueGuideStore((state) => state.openItemId);
  const closeGuide = useBasicValueGuideStore((state) => state.close);
  const isManual = openItemId !== null;

  const { pendingFeatures, areas, fleetId, refetch, onClose, markDismissed, snooze } = useBasicValueOnboardingSource({ forceActive: isManual });
  const startCursorFlow = useCursorFlowStore((state) => state.start);

  // The checklist drawer is hosted here (not in the footer action) so the dialog chrome can
  // switch to side-panel mode without remounting the drawer. `advanceRef` bridges the step
  // navigation (owned by the dialog view) to the drawer's success callback.
  const [checklistDrawerOpen, setChecklistDrawerOpen] = useState(false);
  const advanceRef = useRef<() => void>(() => {});

  // Manual-only: opened on demand from the home maturity hero or the Basic Value page. There is no
  // auto-popup anymore — the home hero is the entry point.
  if (!isManual) return null;

  // Same order as the Basic Value page (MATURITY_ITEMS order, preserved by pendingFeatures).
  const steps: BasicValueStep[] = pendingFeatures.map((pending: BasicValuePendingFeature) => {
    const rec = pending.recommendation;
    return {
      id: pending.id,
      icon: ITEM_ICON[pending.id],
      title: rec?.journeyName || t(`items.${pending.id}.title`),
      subtitle: rec?.shortDescription || rec?.description || t(`items.${pending.id}.description`),
      benefit: t(`benefits.${pending.id}`),
      criticality: rec?.criticality ? CRITICALITY_MAP[rec.criticality] : undefined,
      criticalityLabel: rec?.criticality ? t(`criticality.${CRITICALITY_MAP[rec.criticality]}`) : undefined,
      reasons: rec?.reasons?.length ? rec.reasons : undefined,
    };
  });

  if (steps.length === 0) return null;

  const initialStepIndex = isManual
    ? Math.max(
        0,
        steps.findIndex((step) => step.id === openItemId)
      )
    : 0;
  const routeById = new Map(pendingFeatures.map((feature) => [feature.id, feature.route]));

  const handleStepChange = (featureId: MaturityItemId, stepIndex: number) => {
    Analytics.track('BasicValueModalStepViewed', { fleetId, feature: featureId, stepIndex, totalSteps: steps.length, source: isManual ? 'page' : 'auto' });
  };

  const handleClose = () => {
    Analytics.track('BasicValueModalClosed', { fleetId, source: isManual ? 'page' : 'auto' });
    closeGuide();
    onClose();
  };

  const handleSnooze = () => {
    Analytics.track('BasicValueModalDontShowAgainClicked', { fleetId });
    closeGuide();
    snooze();
  };

  // Advancing past a step means the user resolved it (inline create succeeded or hit the action CTA).
  // Advancing past the LAST step means they went through the whole guided flow — track it as completed.
  const handleAdvance = (step: BasicValueStep, helpers: BasicValueStepHelpers) => {
    // Refresh the entity counts so the step the user just filled flips to "done" instead of staying pending.
    refetch();
    const source = isManual ? 'page' : 'auto';
    if (helpers.isLast) {
      Analytics.track('BasicValueModalCompleted', { fleetId, totalSteps: steps.length, source });
      handleClose();
      return;
    }
    const stepIndex = steps.findIndex((s) => s.id === step.id);
    Analytics.track('BasicValueModalStepCompleted', { fleetId, feature: step.id, stepIndex, totalSteps: steps.length, source });
    helpers.goNext();
  };

  // Items without an inline step navigate to their page (geofences); closing the modal + a cursor
  // flow guides the user to the action to take there.
  const renderNavigateAction = (step: BasicValueStep) => {
    const route = routeById.get(step.id);
    if (!route) return null;
    return (
      <Button
        onClick={() => {
          Analytics.track('BasicValueModalGoToFeatureClicked', { fleetId, feature: step.id, route });
          handleClose();
          history.push(route);
          const tourSteps = buildItemTourSteps(step.id, t);
          if (tourSteps) startCursorFlow(tourSteps);
        }}
      >
        {t('actions.configure')}
      </Button>
    );
  };

  return (
    <>
      <DialogBasicValueOnboardingView
        key={openItemId ?? 'auto'}
        steps={steps}
        initialStepIndex={initialStepIndex}
        onClose={handleClose}
        onStepChange={handleStepChange}
        sidePanelOpen={checklistDrawerOpen}
        onDismissSnooze={isManual ? undefined : handleSnooze}
        renderTrack={(step) => <MaturityTrackCompact areas={areas} activeAreaKey={AREA_BY_ITEM_ID[step.id]} />}
        renderStepBody={(step, helpers) => {
          // After an inline step is resolved, advance straight to the next one (tracks completion, or closes if last).
          const advance = () => handleAdvance(step, helpers);
          switch (step.id) {
            case 'devices':
              return <InstallationStepBody fleetId={fleetId} onLaunch={handleClose} />;
            case 'drivers':
              return <DriverStepBody fleetId={fleetId} onRegistered={markDismissed} onCompleted={advance} />;
            case 'vehicles':
              return <VehicleStepBody fleetId={fleetId} onCompleted={advance} />;
            case 'device-association':
              return <DeviceAssociationStepBody fleetId={fleetId} onCompleted={advance} />;
            case 'driver-association':
              return <DriverAssociationStepBody fleetId={fleetId} onCompleted={advance} />;
            case 'users':
              return <UsersStepBody fleetId={fleetId} onCompleted={advance} />;
            case 'groups':
              return <GroupStepBody fleetId={fleetId} onCompleted={advance} />;
            case 'speed':
              return <SpeedStepBody fleetId={fleetId} onCompleted={advance} />;
            default:
              return null;
          }
        }}
        renderStepAction={(step, helpers) => {
          // After the step's create flow succeeds, advance straight to the next step (tracks completion, or closes if last).
          const advance = () => handleAdvance(step, helpers);
          if (NAVIGATE_IDS.has(step.id)) return renderNavigateAction(step);
          switch (step.id) {
            case 'devices':
            case 'drivers':
            case 'vehicles':
            case 'device-association':
            case 'driver-association':
            case 'users':
            case 'groups':
            case 'speed':
              // Resolved inline in the body (devices = installation list); footer keeps only nav/close.
              return null;
            case 'alerts':
              return <AlertStepAction fleetId={fleetId} onCompleted={advance} />;
            case 'checklists':
              return (
                <ChecklistStepAction
                  fleetId={fleetId}
                  onCreate={() => {
                    advanceRef.current = advance;
                    setChecklistDrawerOpen(true);
                  }}
                />
              );
            default:
              return renderNavigateAction(step);
          }
        }}
      />
      <ChecklistDrawer
        open={checklistDrawerOpen}
        setOpen={setChecklistDrawerOpen}
        hideTrigger
        disableSurvey
        onSuccess={() => {
          // Drawer closes itself; advance the guide to the next step.
          advanceRef.current();
        }}
      />
    </>
  );
};

const BasicValueOnboarding = () => {
  const { session } = useUser();
  // The source hook reads getSession()/fleet; only mount once a session exists.
  if (!session) return null;
  return <BasicValueOnboardingConnected />;
};

export default BasicValueOnboarding;
